import * as Sentry from '@sentry/react'
import React from 'react'
import ErrorPage from './error.page'

interface ErrorBoundaryProps {
  children: React.ReactNode
}

interface ErrorBoundaryState {
  hasError: boolean
  error?: Error
  errorInfo?: React.ErrorInfo
}

/**
 * ErrorBoundary Component
 *
 * This class component acts as an error boundary in a React application.
 * It catches JavaScript errors anywhere in its child component tree,
 * logs those errors, and displays a fallback UI (`ErrorPage`) instead
 * of crashing the entire application.
 *
 * This ensures the app remains functional and provides a user-friendly error page.
 *
 */

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    // Log error to console or error reporting service
    console.error('Error caught by boundary:', error, errorInfo)

    // Report to Sentry with the React component stack attached
    Sentry.withScope((scope) => {
      scope.setExtra('componentStack', errorInfo.componentStack)
      Sentry.captureException(error)
    })

    // Store error and errorInfo in state
    this.setState({ error, errorInfo })
  }

  render(): React.ReactNode {
    if (this.state.hasError) return <ErrorPage errorMsg={this.state.error!} errorInfo={this.state.errorInfo!} />

    return this.props.children
  }
}

export default ErrorBoundary
